import { PlusIcon } from 'lucide-react';
import { type RefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { appConfig } from 'shared';
import { getMenuSection } from '~/lib/entity-modules';
import { CountBadge } from '~/modules/common/count-badge';
import { EntityAvatar } from '~/modules/common/entity-avatar';
import type { UserMenuItem } from '~/modules/me/types';
import { menuOrganizations } from '~/modules/navigation/menu-sheet/helpers/menu-organizations';
import { sortAndFilterMenu } from '~/modules/navigation/menu-sheet/helpers/sort-and-filter-menu';
import { useMenu } from '~/modules/navigation/menu-sheet/helpers/use-menu';
import { useOrganizationUnseenCounts } from '~/modules/navigation/menu-sheet/helpers/use-menu-item-unseen-count';
import { SidebarNavButton } from '~/modules/navigation/nav-buttons';
import { useNavigationStore } from '~/modules/navigation/navigation-store';
import type { TriggerOrganizationFn } from '~/modules/navigation/types';
import { useRouteOrganizationId } from '~/modules/navigation/use-route-organization-id';
import { SidebarMenu } from '~/modules/ui/sidebar';
import { useUserStore } from '~/modules/user/user-store';
import { cn } from '~/utils/cn';

interface SidebarOrganizationButtonProps {
  organization: UserMenuItem;
  /** 0 hides the badge. */
  unseenCount: number;
  /** Its menu sheet is open. */
  isActive: boolean;
  /** Its pages are open. */
  isCurrent: boolean;
  isCollapsed: boolean;
  onClick: TriggerOrganizationFn;
}

function SidebarOrganizationButton({ organization, unseenCount, isActive, isCurrent, isCollapsed, onClick }: SidebarOrganizationButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <SidebarNavButton
      ref={buttonRef}
      label={organization.name}
      isCollapsed={isCollapsed}
      isActive={isActive}
      isCurrent={isCurrent}
      onClick={() => onClick(organization, buttonRef)}
      className="relative"
      itemClassName="pb-0"
      // Its right edge stays put and a longer count grows left: the button clips what passes its edge
      badge={unseenCount > 0 && <CountBadge count={unseenCount} className="absolute top-2 left-12 -translate-x-full" />}
    >
      <EntityAvatar
        // An outline, not a ring: its offset stays transparent over the button's open fill
        className={cn(
          'size-8 shrink-0 transition-transform group-hover:scale-110',
          isCurrent && 'outline-primary outline-2 outline-offset-2',
          // Only the organization you are in, or are opening, shows its color
          !isCurrent &&
            !isActive &&
            'pointer-fine:group-[:not(:hover,:focus-visible)]:resting-avatar pointer-coarse:group-[:not(:focus-visible)]:resting-avatar',
        )}
        type={organization.entityType}
        id={organization.id}
        name={organization.name}
        url={organization.thumbnailUrl}
      />
    </SidebarNavButton>
  );
}

interface SidebarCreateOrganizationButtonProps {
  isCollapsed: boolean;
  onClick: (triggerRef: RefObject<HTMLButtonElement | null>) => void;
}

/** Ends the rail's organizations: the way into creating one, and the only button there for a user who has none yet. */
function SidebarCreateOrganizationButton({ isCollapsed, onClick }: SidebarCreateOrganizationButtonProps) {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <SidebarNavButton
      ref={buttonRef}
      label={t('c:create_resource', { resource: t('c:organization') })}
      isCollapsed={isCollapsed}
      onClick={() => onClick(buttonRef)}
      itemClassName="pb-0"
    >
      <span className="border-sidebar-foreground/30 more-contrast:border-sidebar-foreground/40 flex size-8 shrink-0 items-center justify-center rounded-md border border-dashed transition-transform group-hover:scale-110">
        {/* The nav icons' rendered stroke: theirs is drawn at size-5, this one at size-4 */}
        <PlusIcon className="size-4" strokeWidth={(appConfig.theme.strokeWidth * 5) / 4} />
      </span>
    </SidebarNavButton>
  );
}

interface SidebarOrganizationButtonsProps {
  isCollapsed: boolean;
  onClick: TriggerOrganizationFn;
}

/** A rail button per active organization membership in menu order, scrolling on its own once the rail runs out of room. */
export function SidebarOrganizationButtons({ isCollapsed, onClick }: SidebarOrganizationButtonsProps) {
  const user = useUserStore((state) => state.user);
  const navSheetOpen = useNavigationStore((state) => state.navSheetOpen);
  const navSheetOrganizationId = useNavigationStore((state) => state.navSheetOrganizationId);

  const { menu } = useMenu(user?.id);
  const organizations = menuOrganizations(menu);
  const routeOrganizationId = useRouteOrganizationId(organizations);
  const unseenCounts = useOrganizationUnseenCounts(menu);
  const createOrganization = getMenuSection('organization')?.createAction;

  // An open menu sheet shows these counts itself: the full menu on its rows, an organization's sheet on its header and sections
  const isFullMenuOpen = navSheetOpen === 'menu' && !navSheetOrganizationId;

  return (
    <SidebarMenu className="no-scrollbar min-h-0 gap-0 overflow-y-auto">
      {sortAndFilterMenu(organizations, false).map((organization) => {
        const isActive = navSheetOpen === 'menu' && navSheetOrganizationId === organization.id;
        return (
          <SidebarOrganizationButton
            key={organization.id}
            organization={organization}
            unseenCount={isFullMenuOpen || isActive ? 0 : (unseenCounts.get(organization.id) ?? 0)}
            isActive={isActive}
            isCurrent={organization.id === routeOrganizationId}
            isCollapsed={isCollapsed}
            onClick={onClick}
          />
        );
      })}
      {createOrganization && <SidebarCreateOrganizationButton isCollapsed={isCollapsed} onClick={createOrganization} />}
    </SidebarMenu>
  );
}
